import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, CardBody, CardHeader, Input, Spinner } from '@components/ui';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { certificateBundleService } from '../services/certificateBundleService';
import { useBundlePrice } from '../hooks/useBundlePrice';

interface BundleLocationState {
  planId?: string;
  planName?: string;
  planPrice?: number;
}

const MAX_FILE_SIZE = 4 * 1024 * 1024; // igual que el portal de certificados
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

/** Mismas reglas que el formulario público, para que el registry no rechace. */
function validarArchivo(file: File | null, etiqueta: string): string | undefined {
  if (!file) return `${etiqueta} es requerido`;
  if (file.size > MAX_FILE_SIZE) return `${etiqueta} no debe exceder 4MB`;
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return `${etiqueta} debe ser PDF o imagen (JPG, PNG, GIF, WEBP)`;
  }
  return undefined;
}

interface FileFieldProps {
  label: string;
  file: File | null;
  error?: string;
  onChange: (file: File | null) => void;
}

function FileField({ label, file, error, onChange }: FileFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
        {label}
        <span className="text-danger-500 ml-1">*</span>
      </label>
      <input
        type="file"
        accept=".pdf,image/*"
        onChange={(event) => onChange(event.target.files?.[0] || null)}
        className="block w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 file:mr-4 file:rounded-md file:border-0 file:bg-primary-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-primary-700"
      />
      {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
      {file && !error && (
        <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
          {file.name} · {(file.size / 1024).toFixed(0)} KB
        </p>
      )}
      <p className="mt-1 text-xs text-secondary-400 dark:text-secondary-500">
        PDF o imagen, máximo 4MB
      </p>
    </div>
  );
}

export function CertificateBundlePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as BundleLocationState;

  const [fullName, setFullName] = useState('');
  const [telefono, setTelefono] = useState('');
  const [avisoOperacion, setAvisoOperacion] = useState<File | null>(null);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isStateValid = useMemo(() => Boolean(state.planId), [state.planId]);
  const planPrice = state.planPrice ?? 0;
  const { price: bundlePrice } = useBundlePrice();
  const bundlePriceLabel = bundlePrice != null ? `$${bundlePrice.toFixed(2)}` : '—';

  // El bundle cobra los dos productos en un solo cargo. El total se calcula acá
  // solo para mostrarlo: el monto que se cobra lo fija el servidor, que vuelve a
  // sumarlo desde el catálogo y nunca lo acepta del cliente.
  const totalBundle = bundlePrice != null ? bundlePrice + planPrice : null;
  const totalLabel = totalBundle != null ? `$${totalBundle.toFixed(2)}` : '—';

  // Datos que el portal ya conoce: no se le vuelven a pedir al usuario.
  const { data: prefill, isLoading: isLoadingPrefill } = useQuery({
    queryKey: ['bundle', 'prefill'],
    queryFn: () => certificateBundleService.getPrefill(),
  });

  const necesitaTelefono = prefill?.faltantes.includes('telefono') ?? false;
  const datosEmpresaIncompletos = (prefill?.faltantes ?? []).filter(f => f !== 'telefono');

  const clearError = (field: string) =>
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const handleSubmit = async () => {
    const next: Record<string, string | undefined> = {};

    if (fullName.trim().length < 3) {
      next.fullName = 'El nombre debe tener al menos 3 caracteres';
    }
    if (necesitaTelefono && !telefono.trim()) {
      next.telefono = 'El teléfono es requerido';
    }
    next.avisoOperacion = validarArchivo(avisoOperacion, 'El aviso de operación');
    if (!aceptaTerminos) {
      next.aceptaTerminos = 'Debes aceptar los Términos y Condiciones';
    }

    const limpio = Object.fromEntries(Object.entries(next).filter(([, v]) => v));
    if (Object.keys(limpio).length > 0) {
      setErrors(limpio);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      const response = await certificateBundleService.registerBundle({
        fullName: fullName.trim(),
        telefono: telefono.trim() || undefined,
        avisoOperacion: avisoOperacion!,
      });

      sessionStorage.setItem(
        'certificate_bundle_request',
        JSON.stringify({
          planId: state.planId,
          planName: state.planName,
          registroId: response.data.id,
          createdAt: new Date().toISOString(),
        })
      );

      navigate('/subscriptions/plans', {
        state: { bundlePlanId: state.planId, bundleRegistroId: response.data.id },
        replace: true,
      });
    } catch (error: any) {
      setSubmitError(
        error?.response?.data?.message || 'No se pudo registrar el certificado. Intenta de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Certificado de Firma Electrónica
        </h1>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Usamos los datos de tu empresa. Solo necesitamos saber quién tramita y tu
          aviso de operación.
        </p>
      </div>

      {!isStateValid && (
        <Alert variant="warning" title="No se pudo identificar el plan">
          Regresa a la lista de planes y selecciona el bundle nuevamente.
        </Alert>
      )}

      {submitError && (
        <Alert variant="danger" title="No pudimos registrar tu certificado">
          {submitError}
        </Alert>
      )}

      {datosEmpresaIncompletos.length > 0 && (
        <Alert variant="warning" title="Faltan datos de tu empresa">
          No encontramos: {datosEmpresaIncompletos.join(', ')}. Complétalos en la
          configuración de tu empresa antes de continuar.
        </Alert>
      )}

      {/* Resumen del bundle */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Resumen del Bundle
          </h2>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex items-center justify-between text-sm text-secondary-700 dark:text-secondary-300">
            <span>Certificado de Firma Electrónica</span>
            <span>{bundlePriceLabel}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-secondary-700 dark:text-secondary-300">
            <span>{state.planName || 'Plan de Factura Libre'}</span>
            <span>${planPrice.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-secondary-200 dark:border-secondary-700 pt-3 text-base font-semibold text-primary-700 dark:text-primary-300">
            <span>Total a pagar</span>
            <span>{totalLabel}</span>
          </div>
          <p className="text-xs text-secondary-600 dark:text-secondary-400">
            Un solo cargo a tu tarjeta por los dos productos.
          </p>
        </CardBody>
      </Card>

      {/* Datos que ya tenemos */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Datos de tu Empresa
          </h2>
        </CardHeader>
        <CardBody>
          {isLoadingPrefill ? (
            <div className="flex items-center gap-2 text-sm text-secondary-500">
              <Spinner size="sm" /> Cargando datos de tu empresa…
            </div>
          ) : prefill ? (
            <div className="space-y-3">
              <p className="flex items-start gap-2 text-xs text-success-700 dark:text-success-400">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                Estos datos se envían automáticamente. Si algo está mal, corrígelo en la
                configuración de tu empresa.
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[
                  ['Razón social', prefill.company.razonSocial],
                  ['RUC', prefill.company.ruc ? `${prefill.company.ruc}-${prefill.company.dv}` : ''],
                  ['Tipo de persona', prefill.company.tipoPersona === 'NATURAL' ? 'Natural' : 'Jurídica'],
                  ['Correo', prefill.company.email],
                  ['Teléfono', prefill.company.telefono],
                  [
                    'Ubicación fiscal',
                    prefill.ubicacion.disponible
                      ? `Código ${prefill.ubicacion.codigoUbicacion ?? ''}`
                      : 'La completará nuestro equipo',
                  ],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-2 border-b border-secondary-100 dark:border-secondary-800 py-1">
                    <dt className="text-secondary-500 dark:text-secondary-400">{label}</dt>
                    <dd className="font-medium text-secondary-900 dark:text-white text-right">
                      {value || <span className="text-warning-600">Falta</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : (
            <Alert variant="warning">No pudimos cargar los datos de tu empresa.</Alert>
          )}
        </CardBody>
      </Card>

      {/* Lo que sí hay que pedir */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Responsable del Trámite
          </h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <Input
            label="Nombre completo del responsable"
            value={fullName}
            onChange={(e) => { setFullName(e.target.value); clearError('fullName'); }}
            error={errors.fullName}
            placeholder="Nombre de quien tramita el certificado"
            required
          />
          {necesitaTelefono && (
            <Input
              label="Teléfono de contacto"
              value={telefono}
              onChange={(e) => { setTelefono(e.target.value); clearError('telefono'); }}
              error={errors.telefono}
              placeholder="+507 6123 4567"
              helperText="Tu empresa no tiene teléfono registrado. Lo usaremos para coordinar la emisión."
              required
            />
          )}
          <FileField
            label="Aviso de Operación"
            file={avisoOperacion}
            error={errors.avisoOperacion}
            onChange={(f) => { setAvisoOperacion(f); clearError('avisoOperacion'); }}
          />

          <div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={aceptaTerminos}
                onChange={(e) => { setAceptaTerminos(e.target.checked); clearError('aceptaTerminos'); }}
                className="mt-0.5 h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700 dark:text-secondary-300">
                Acepto los{' '}
                <a
                  href="https://certificados.facturalibre.io/terminos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  Términos y Condiciones
                </a>{' '}
                de emisión del certificado
                <span className="text-danger-500 ml-1">*</span>
              </span>
            </label>
            {errors.aceptaTerminos && (
              <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
                {errors.aceptaTerminos}
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={() => navigate('/subscriptions/plans')}>
          Volver a planes
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            !isStateValid ||
            isSubmitting ||
            isLoadingPrefill ||
            bundlePrice == null ||
            datosEmpresaIncompletos.length > 0
          }
        >
          {isSubmitting ? 'Registrando...' : `Continuar al pago — ${totalLabel}`}
        </Button>
      </div>
    </div>
  );
}
